import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { RESOURCE_TYPE_COLORS } from '../../constants/colors'

const NODE_TYPES = [
  { type: 'journal',  label: 'Journal Article', shape: 'star',           color: RESOURCE_TYPE_COLORS.journal  || '#8B5CF6' },
  { type: 'video',    label: 'Video',           shape: 'hexagon',        color: RESOURCE_TYPE_COLORS.video    || '#EF4444' },
  { type: 'audio',    label: 'Audio',           shape: 'pentagon',       color: RESOURCE_TYPE_COLORS.audio    || '#3B82F6' },
  { type: 'image',    label: 'Image',           shape: 'ellipse',        color: RESOURCE_TYPE_COLORS.image    || '#22C55E' },
  { type: 'website',  label: 'Website',         shape: 'roundrectangle', color: RESOURCE_TYPE_COLORS.website  || '#14B8A6' },
  { type: 'course',   label: 'Course',          shape: 'diamond',        color: RESOURCE_TYPE_COLORS.course   || '#F59E0B' },
  { type: 'document', label: 'Document',        shape: 'rectangle',      color: RESOURCE_TYPE_COLORS.document || '#6B7280' },
]

const EDGE_TYPES = [
  { type: 'similarity',   label: 'Similarity',    lineClass: 'border-dashed border-purple-500' },
  { type: 'citation',     label: 'Citation',       lineClass: 'border-solid border-gray-500' },
  { type: 'same-author',  label: 'Same Author',    lineClass: 'border-dotted border-blue-500' },
  { type: 'cross-domain', label: 'Cross-domain',   lineClass: 'border-solid border-acad-500' },
  { type: 'shared-topic', label: 'Shared Topic',   lineClass: 'border-solid border-teal-500' },
]

// SVG shape rendering for the legend
const ShapeIcon: React.FC<{ shape: string; color: string; size?: number }> = ({
  shape,
  color,
  size = 14,
}) => {
  const half = size / 2
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {shape === 'star' && (
        <polygon
          points={`${half},1 ${half + 2},${half - 2} ${size - 1},${half - 2} ${half + 3},${half + 1} ${half + 4},${size - 1} ${half},${half + 3} ${half - 4},${size - 1} ${half - 3},${half + 1} 1,${half - 2} ${half - 2},${half - 2}`}
          fill={color}
        />
      )}
      {shape === 'hexagon' && (
        <polygon
          points={`${half},1 ${size - 2},${half * 0.5} ${size - 2},${size - half * 0.5} ${half},${size - 1} 2,${size - half * 0.5} 2,${half * 0.5}`}
          fill={color}
        />
      )}
      {shape === 'pentagon' && (
        <polygon
          points={`${half},1 ${size - 1},${half - 1} ${size - 2},${size - 2} 2,${size - 2} 1,${half - 1}`}
          fill={color}
        />
      )}
      {shape === 'ellipse' && (
        <ellipse cx={half} cy={half} rx={half - 1} ry={half - 1} fill={color} />
      )}
      {shape === 'roundrectangle' && (
        <rect x="1" y="1" width={size - 2} height={size - 2} rx="3" fill={color} />
      )}
      {shape === 'diamond' && (
        <polygon
          points={`${half},1 ${size - 1},${half} ${half},${size - 1} 1,${half}`}
          fill={color}
        />
      )}
      {shape === 'rectangle' && (
        <rect x="1" y="2" width={size - 2} height={size - 4} fill={color} />
      )}
    </svg>
  )
}

const ConnectionsLegend: React.FC = () => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="absolute bottom-3 left-3 z-20">
      <button
        onClick={() => setExpanded(!expanded)}
        className="bg-white/95 backdrop-blur rounded-lg border border-gray-200 shadow-sm
                   px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider
                   hover:border-acad-300 transition-colors flex items-center gap-1"
      >
        Legend
        {expanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronUp className="w-3 h-3" />
        )}
      </button>

      {expanded && (
        <div className="mt-1 bg-white/95 backdrop-blur rounded-xl border border-gray-200 shadow-sm p-3 min-w-[180px]">
          {/* Resource types */}
          <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Resource Types
          </h4>
          <div className="space-y-1 mb-3">
            {NODE_TYPES.map(nt => (
              <div key={nt.type} className="flex items-center gap-2">
                <ShapeIcon shape={nt.shape} color={nt.color} />
                <span className="text-[10px] text-gray-600">{nt.label}</span>
              </div>
            ))}
          </div>

          {/* Edge types */}
          <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Connection Types
          </h4>
          <div className="space-y-1">
            {EDGE_TYPES.map(et => (
              <div key={et.type} className="flex items-center gap-2">
                <span className={`w-4 h-0 border-t-2 ${et.lineClass} inline-block`} />
                <span className="text-[10px] text-gray-600">{et.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ConnectionsLegend
